import { IconButton } from '../ui';
import { CancelIcon, ConfirmIcon } from './icons';
import styles from './ComposerPill.module.css';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Figma "Voice Recording" (14701:126431 / L 14701:126554): a left-packed cluster of thin bars driven
// by the live mic level (see use-dictation `levels`), with the timer pushed to the track's right edge.
// Two sizes — the compact floating panel keeps the design's small thin bars; the full-size chat scales
// them up so the waveform reads on the wider composer.
type WaveSize = { count: number; barW: number; gap: number; band: number; min: number; max: number };
// Small/compact chat keeps the thinnest bars; the full-size chat scales them up.
const COMPACT: WaveSize = { count: 44, barW: 1, gap: 1.5, band: 16, min: 3, max: 14 };
const FULL: WaveSize = { count: 56, barW: 2.5, gap: 2, band: 26, min: 4, max: 22 };

export function RecordingBar({
  seconds,
  levels,
  cancelLabel,
  sendLabel,
  onCancel,
  onConfirm,
  full = false,
}: {
  seconds: number;
  levels: number[];
  cancelLabel: string;
  sendLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  // The full-size chat scales the waveform up; the compact panel keeps the small design bars.
  full?: boolean;
}) {
  const s = full ? FULL : COMPACT;
  // Newest levels, right-most, packed into a fixed cluster (front-padded with silence).
  const padded = [...Array(Math.max(0, s.count - levels.length)).fill(0), ...levels.slice(-s.count)];
  return (
    <>
      <IconButton
        size="small"
        variant="text"
        onClick={onCancel}
        aria-label={cancelLabel}
        title={cancelLabel}
        className={styles.chatRound}
      >
        <CancelIcon />
      </IconButton>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: full ? 36 : 30,
          display: 'flex',
          alignItems: 'center',
          // Waveform hugs the left; the timer is pushed to the track's right edge.
          justifyContent: 'space-between',
          gap: 12,
          padding: '0 14px',
          borderRadius: 999,
          background: 'var(--ds-surface-standard-bg)',
        }}
      >
        <div aria-hidden style={{ flexShrink: 1, minWidth: 0, height: s.band, display: 'flex', alignItems: 'center', gap: s.gap, overflow: 'hidden' }}>
          {padded.map((lvl, i) => (
            <span
              key={i}
              // No canned animation — the height IS the live mic level, so the bars react to the voice.
              style={{
                flexShrink: 0,
                width: s.barW,
                height: Math.min(s.max, Math.max(s.min, lvl * s.max)),
                borderRadius: s.barW / 2,
                background: 'var(--ds-text-primary)',
                transition: 'height 90ms linear',
              }}
            />
          ))}
        </div>
        <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 400, color: 'var(--ds-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {formatElapsed(seconds)}
        </span>
      </div>
      <IconButton
        size="small"
        variant="contained"
        color="standard"
        onClick={onConfirm}
        aria-label={sendLabel}
        title={sendLabel}
        className={styles.chatRound}
      >
        <ConfirmIcon />
      </IconButton>
    </>
  );
}
